// Fila de follow-ups: todo negócio do pipeline privado na etapa Follow-up, com
// onde está no ciclo, como é feito (desligado / tarefa / e-mail automático) e o
// que falta para o e-mail sair. O que pede ação vem primeiro.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  App as AntApp, Alert, Button, DatePicker, Input, Modal, Select, Space, Table, Tag, Tooltip, Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import axios from 'axios';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { fmtDataBr, fmtMomentoBr } from '../../utils/dataFmt';
import { valorCompacto } from '../comercial-pipeline/saudeNegocio';
import { usePerms } from '../../hooks/usePerms';
import { fu, erroDe, type ItemFila, type Modo } from './api';
import { COR_SITUACAO, ROTULO_MODO, contarPorSituacao, fraseDaRodada, ordenarFila } from './regras';

const { Text } = Typography;

const FILTROS: { chave: string; rotulo: string; codigos: string[] }[] = [
  { chave: 'acao', rotulo: 'Pedem ação', codigos: ['pronto_para_perder', 'devido', 'tarefa_aberta', 'sem_data'] },
  { chave: 'ciclo', rotulo: 'No ciclo', codigos: ['agendado', 'aguardando_resposta', 'pausado'] },
  { chave: 'desligado', rotulo: 'Desligados', codigos: ['desligado', 'fora'] },
  { chave: 'todos', rotulo: 'Todos', codigos: [] },
];

export default function AbaFila() {
  const { message } = AntApp.useApp();
  const { pode } = usePerms();
  const podeOperar = pode('comercial.pipeline.editar_etapa_funil') || pode('admin.permissoes.editar');
  const navigate = useNavigate();
  const [dados, setDados] = useState<Awaited<ReturnType<typeof fu.fila>> | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [filtro, setFiltro] = useState('acao');
  const [busca, setBusca] = useState('');
  const [mexendo, setMexendo] = useState<number | null>(null);
  const [respondendo, setRespondendo] = useState<ItemFila | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try { setDados(await fu.fila()); } catch (e) { message.error(`Não consegui carregar a fila: ${erroDe(e)}`); }
    finally { setCarregando(false); }
  }, [message]);
  useEffect(() => { carregar(); }, [carregar]);

  const itens = useMemo(() => ordenarFila(dados?.itens || []), [dados]);
  const contagem = useMemo(() => contarPorSituacao(itens), [itens]);
  const visiveis = useMemo(() => {
    const f = FILTROS.find(x => x.chave === filtro);
    const b = busca.trim().toLowerCase();
    return itens.filter(i => (!f || !f.codigos.length || f.codigos.includes(i.situacao.codigo))
      && (!b || `${i.titulo} ${i.cliente || ''} ${i.responsavel || ''} ${i.numero_proposta || ''}`.toLowerCase().includes(b)));
  }, [itens, filtro, busca]);

  const definir = async (i: ItemFila, mudanca: { modo?: Modo; pausado_ate?: string | null }) => {
    setMexendo(i.id);
    try { await fu.definir(i.id, mudanca); await carregar(); }
    catch (e) { message.error(erroDe(e)); }
    finally { setMexendo(null); }
  };

  const trocarContato = async (i: ItemFila, contatoId: number) => {
    setMexendo(i.id);
    try {
      const token = await getToken();
      await axios.put(`${API_URL}/comercial/propostas/${i.id}`, { contato_id: contatoId }, { headers: { Authorization: token } });
      message.success('Contato do negócio trocado');
      await carregar();
    } catch (e) { message.error(erroDe(e)); }
    finally { setMexendo(null); }
  };

  const abrirNegocio = (id: number) => {
    try { sessionStorage.setItem('abrir_proposta_id', String(id)); } catch { /* sem storage */ }
    navigate('/comercial/pipeline');
  };

  const max = dados?.config.max_tentativas || 3;
  const colunas: ColumnsType<ItemFila> = [
    {
      title: 'Negócio', key: 'negocio', width: 300,
      render: (_, i) => (
        <div style={{ minWidth: 0 }}>
          <Button type="link" size="small" style={{ padding: 0, height: 'auto', whiteSpace: 'normal', textAlign: 'left' }}
            onClick={() => abrirNegocio(i.id)}>{i.titulo}</Button>
          <div><Text type="secondary" style={{ fontSize: 12 }}>
            {[i.cliente, i.numero_proposta, i.valor_estimado ? valorCompacto(i.valor_estimado) : null].filter(Boolean).join(' · ')}
          </Text></div>
        </div>
      ),
    },
    {
      title: 'Situação', key: 'situacao', width: 230,
      render: (_, i) => (
        <Space orientation="vertical" size={2}>
          <Tag color={COR_SITUACAO[i.situacao.codigo] || 'default'} style={{ marginInlineEnd: 0 }}>{i.situacao.rotulo}</Tag>
          {i.modo && i.modo !== 'desligado' && (
            <Text type="secondary" style={{ fontSize: 12 }}>{i.tentativas}/{max} feitos
              {i.ultimo_followup_em ? ` · último ${fmtDataBr(i.ultimo_followup_em)}` : ''}</Text>
          )}
          {i.legado && i.modo === 'desligado' && <Text type="secondary" style={{ fontSize: 12 }}>do estoque — decidir na aba Limpeza</Text>}
        </Space>
      ),
    },
    {
      title: 'Como', key: 'modo', width: 190,
      render: (_, i) => (
        <Select size="small" style={{ width: 175 }} value={i.modo || undefined} placeholder="Desligado"
          disabled={!podeOperar || mexendo === i.id} loading={mexendo === i.id}
          options={(['desligado', 'manual', 'automatico'] as Modo[]).map(m => ({ value: m, label: ROTULO_MODO[m] }))}
          onChange={(m: Modo) => definir(i, { modo: m })} />
      ),
    },
    {
      title: 'Para quem', key: 'contato', width: 260,
      render: (_, i) => {
        if (i.contato_email) {
          return <div style={{ minWidth: 0 }}><div>{i.contato_nome}</div><Text type="secondary" style={{ fontSize: 12 }}>{i.contato_email}</Text></div>;
        }
        return (
          <Space orientation="vertical" size={2} style={{ width: '100%' }}>
            <Text type="warning" style={{ fontSize: 12 }}>{i.contato_nome ? `${i.contato_nome} — sem e-mail` : 'Sem contato no negócio'}</Text>
            {i.contatos_do_cliente.length > 0 && podeOperar && (
              <Select size="small" style={{ width: 240 }} placeholder="Usar outro contato da empresa" showSearch optionFilterProp="label"
                disabled={mexendo === i.id}
                options={i.contatos_do_cliente.map(c => ({ value: c.id, label: `${c.nome}${c.cargo ? ` (${c.cargo})` : ''} — ${c.email}` }))}
                onChange={(id: number) => trocarContato(i, id)} />
            )}
          </Space>
        );
      },
    },
    {
      title: 'De', key: 'remetente', width: 190,
      render: (_, i) => i.remetente
        ? <Tooltip title={i.remetente.origem === 'nome' ? `Achado pelo nome do responsável (${i.responsavel})` : 'Vendedor do negócio'}>
            <div style={{ minWidth: 0 }}><div>{i.remetente.nome}</div><Text type="secondary" style={{ fontSize: 12 }}>{i.remetente.email}</Text></div>
          </Tooltip>
        : <Text type="warning" style={{ fontSize: 12 }}>{i.responsavel ? `${i.responsavel} — e-mail não achado` : 'Sem responsável'}</Text>,
    },
    {
      title: '', key: 'acoes', width: 210,
      render: (_, i) => (
        <Space orientation="vertical" size={4}>
          {i.modo === 'automatico' && i.email_bloqueado_por.length > 0 && (
            <Tooltip title={i.email_bloqueado_por.join('; ')}>
              <Tag color="gold" style={{ marginInlineEnd: 0, whiteSpace: 'normal' }}>e-mail não sai — vira tarefa</Tag>
            </Tooltip>
          )}
          {podeOperar && i.modo && i.modo !== 'desligado' && (
            <Space size={4} wrap>
              <Button size="small" onClick={() => setRespondendo(i)}>Cliente respondeu</Button>
              {i.situacao.codigo === 'pausado'
                ? <Button size="small" onClick={() => definir(i, { pausado_ate: null })}>Retomar</Button>
                : <DatePicker size="small" placeholder="Pausar até" format="DD/MM/YYYY" style={{ width: 120 }}
                    disabledDate={d => d.isBefore(dayjs(), 'day')}
                    onChange={d => d && definir(i, { pausado_ate: d.format('YYYY-MM-DD') })} />}
            </Space>
          )}
        </Space>
      ),
    },
  ];

  const ultima = dados?.config.ultima_rodada_em;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {dados && !dados.config.envio_automatico_ativo && (
        <Alert type="info" showIcon message="O e-mail automático está desligado na aba Regras."
          description="Negócio em “E-mail automático” vira tarefa para o responsável até alguém ligar o envio." />
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <Space.Compact>
          {FILTROS.map(f => {
            const n = f.codigos.length ? f.codigos.reduce((s, c) => s + (contagem[c] || 0), 0) : itens.length;
            return <Button key={f.chave} type={filtro === f.chave ? 'primary' : 'default'} onClick={() => setFiltro(f.chave)}>{f.rotulo} ({n})</Button>;
          })}
        </Space.Compact>
        <Input.Search allowClear placeholder="Buscar negócio, cliente, responsável" style={{ width: 300, maxWidth: '100%' }}
          value={busca} onChange={e => setBusca(e.target.value)} />
        <div style={{ flex: 1 }} />
        <Text type="secondary" style={{ fontSize: 12 }}>
          Última rodada: {ultima ? `${fmtMomentoBr(ultima)} — ${fraseDaRodada(dados?.config.ultima_rodada)}` : 'ainda não rodou'}
        </Text>
        <Button onClick={carregar} loading={carregando}>Atualizar</Button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <Table rowKey="id" size="small" loading={carregando} dataSource={visiveis} columns={colunas}
          pagination={{ pageSize: 50, hideOnSinglePage: true, showSizeChanger: false }}
          locale={{ emptyText: filtro === 'acao' ? 'Nenhum follow-up pedindo ação agora.' : 'Nada aqui.' }} />
      </div>
      {respondendo && (
        <ModalRespondeu item={respondendo} onCancelar={() => setRespondendo(null)}
          onConfirmar={async (resumo, pausa) => {
            try { await fu.respondeu(respondendo.id, resumo, pausa); message.success('Registrado. O ciclo recomeça a partir de hoje.'); setRespondendo(null); await carregar(); }
            catch (e) { message.error(erroDe(e)); }
          }} />
      )}
    </div>
  );
}

export function ModalRespondeu({ item, onCancelar, onConfirmar }: {
  item: { titulo: string };
  onCancelar: () => void;
  onConfirmar: (resumo: string, pausarAte: string | null) => Promise<void>;
}) {
  const [resumo, setResumo] = useState('');
  const [pausa, setPausa] = useState<string | null>(null);
  const [gravando, setGravando] = useState(false);
  return (
    <Modal open title="O cliente respondeu" onCancel={onCancelar} destroyOnHidden okText="Registrar" cancelText="Voltar"
      confirmLoading={gravando} width="94vw" style={{ maxWidth: 520, top: 24 }}
      onOk={async () => { setGravando(true); try { await onConfirmar(resumo.trim(), pausa); } finally { setGravando(false); } }}>
      <Text strong style={{ display: 'block', marginBottom: 8 }}>{item.titulo}</Text>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        A contagem recomeça a partir de hoje e a tarefa de follow-up em aberto é concluída. Fica registrado na linha do tempo do negócio.
      </Text>
      <Input.TextArea rows={3} maxLength={500} showCount placeholder="O que ele disse (opcional) — ex.: vai decidir na reunião do dia 20"
        value={resumo} onChange={e => setResumo(e.target.value)} />
      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Text>Próximo contato não antes de</Text>
        <DatePicker format="DD/MM/YYYY" placeholder="(opcional)" disabledDate={d => d.isBefore(dayjs(), 'day')}
          onChange={d => setPausa(d ? d.format('YYYY-MM-DD') : null)} />
      </div>
    </Modal>
  );
}
